import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { FcGoogle } from 'react-icons/fc';
import { FaBell, FaFileInvoice, FaTruck } from 'react-icons/fa';
import {
  auth,
  googleProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { updateProfile } from 'firebase/auth';

export default function Login({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [wantsNotifications, setWantsNotifications] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProfileReminder, setShowProfileReminder] = useState(false);

  const navigate = useNavigate();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (showProfileReminder) {
      const timer = setTimeout(() => {
        navigate('/perfil');
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showProfileReminder, navigate]);

  useEffect(() => {
    if (showProfileReminder) {
      const timer = setTimeout(() => {
        setShowProfileReminder(false);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [showProfileReminder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (!email.trim() || !password.trim() || (mode === 'register' && !name.trim())) {
      setError('Por favor, completa todos los campos requeridos.');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Introduce un email válido.');
      return;
    }

    if (mode === 'register' && password !== password2) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        onLogin({ email: userCredential.user.email, name: userCredential.user.displayName });
        navigate('/');
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: name });

        const userDocRef = doc(db, 'usuarios', userCredential.user.uid);
        await setDoc(userDocRef, {
          displayName: name,
          email: email,
          fechaNacimiento: '',
          telefono: '',
          fechaRegistro: new Date().toISOString(),
          wantsNotifications: wantsNotifications || false,
        });

        if (wantsNotifications) {
          console.log('Usuario quiere recibir notificaciones de Duxbyte Informática');
        }

        onLogin({ email: userCredential.user.email, name });
        setShowProfileReminder(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setInfoMessage('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userDocRef = doc(db, 'usuarios', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          displayName: user.displayName || '',
          email: user.email || '',
          fechaNacimiento: '',
          telefono: '',
          fechaRegistro: new Date().toISOString(),
          wantsNotifications: false,
        });
      }

      onLogin({ email: user.email, name: user.displayName });
      navigate('/');
    } catch (err) {
      setError('Error en el inicio con Google: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfoMessage('');

    if (!email.trim()) {
      setError('Introduce tu correo electrónico para recuperar la contraseña.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setInfoMessage('Email de recuperación enviado. Revisa tu bandeja.');
    } catch (err) {
      setError('Error al enviar email: ' + err.message);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCardWrapper}>
        <div className={styles.leftColumn}>
          <div className={styles.infoBoxLeft}>
            <h3 className={styles.infoTitle}>¿Por qué crear una cuenta?</h3><br />
            <ul className={styles.benefitsList}>
              <li><FaTruck className={styles.iconLarge} /> Gestiona tus pedidos fácilmente.</li>
              <li><FaBell className={styles.iconLarge} /> Recibe notificaciones importantes de Duxbyte.</li>
              <li><FaFileInvoice className={styles.iconLarge} /> Accede a tus facturas en línea.</li>
            </ul>
          </div>
        </div>

        <div className={styles.formBox}>
          <form className={styles.loginForm} onSubmit={handleSubmit} noValidate>
            <h2 className={styles.title}>{mode === 'login' ? 'Acceso Clientes' : 'Crear Cuenta'}</h2>

            {error && <p className={styles.error}>{error}</p>}
            {infoMessage && <p className={styles.info}>{infoMessage}</p>}

            {mode === 'register' && (
              <label htmlFor="name" className={styles.label}>
                Nombre completo
                <input
                  id="name"
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre completo"
                  disabled={loading}
                  required
                  autoComplete="name"
                />
              </label>
            )}

            <label htmlFor="email" className={styles.label}>
              Correo electrónico
              <input
                id="email"
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu.email@ejemplo.com"
                disabled={loading}
                autoComplete="username"
                required
              />
            </label>

            <label htmlFor="password" className={styles.label}>
              Contraseña
              <input
                id="password"
                type="password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </label>

            {mode === 'register' && (
              <>
                <label htmlFor="password2" className={styles.label}>
                  Repetir contraseña
                  <input
                    id="password2"
                    type="password"
                    className={styles.input}
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={wantsNotifications}
                    onChange={() => setWantsNotifications(!wantsNotifications)}
                    disabled={loading}
                  />
                  <span className={styles.checkboxText}>
                    Deseo recibir notificaciones de <b>Duxbyte Informática</b>
                  </span>
                </label>
              </>
            )}

            {mode === 'login' && (
              <div className={styles.forgotPassword}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={handleForgotPassword}
                  disabled={loading}
                >
                  ¿He olvidado mi contraseña?
                </button>
              </div>
            )}

            <button
              type="submit"
              className={styles.loginButton}
              disabled={loading}
            >
              {loading ? 'Procesando...' : mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
            </button>

            {mode === 'login' ? (
              <div className={styles.newClient}>
                <p>¿Eres nuevo cliente?</p>
                <button
                  type="button"
                  className={styles.registerButton}
                  onClick={() => {
                    setError('');
                    setInfoMessage('');
                    setMode('register');
                  }}
                  disabled={loading}
                >
                  Crear cuenta con correo electrónico
                </button>
              </div>
            ) : (
              <div className={styles.newClient}>
                <p>¿Ya tienes cuenta?</p>
                <button
                  type="button"
                  className={styles.registerButton}
                  onClick={() => {
                    setError('');
                    setInfoMessage('');
                    setMode('login');
                  }}
                  disabled={loading}
                >
                  Volver a iniciar sesión
                </button>
              </div>
            )}

            {mode === 'login' && (
              <div className={styles.socialLogin}>
                <button
                  type="button"
                  className={styles.googleButton}
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  aria-label="Iniciar sesión con Google"
                >
                  <FcGoogle className={styles.googleIcon} /> Continuar con Google
                </button>
              </div>
            )}
          </form>

          {showProfileReminder && (
            <div className={styles.toastTop}>
              <p>
                🎉 <strong>¡Bienvenido/a a Duxbyte!</strong> Para disfrutar de las ventajas de <b>Duxbyte+</b>,
                es obligatorio completar tu perfil con tu <b>fecha de nacimiento</b> y <b>teléfono</b> desde <i>Mi perfil</i>.
              </p>
              <button onClick={() => navigate('/area-cliente')}>Ir a mi perfil</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
